import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ShieldAlert, LogOut } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, profile, loading, signOut } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-10 h-10 border-4 border-brand-green border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-medium text-brand-black/70">Authenticating session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Account deactivated check
  if (profile && profile.is_active === false) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg border border-brand-border p-6 shadow-sm text-center">
          <div className="w-12 h-12 rounded-full bg-brand-red-light flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-6 h-6 text-brand-red" />
          </div>
          <h2 className="text-xl font-bold text-brand-black mb-2">Account Deactivated</h2>
          <p className="text-sm text-brand-muted mb-6">
            Your staff account has been deactivated by an administrator. Please contact shop management for assistance.
          </p>
          <button
            onClick={() => signOut()}
            className="w-full inline-flex items-center justify-center px-4 py-2 bg-brand-black text-white text-sm font-medium rounded-md hover:bg-brand-black/90 transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
